import "./colors.css";
import { getThemeAwareCssValue } from "./charts/utils";

export const fillColors: { [name: string]: string } = {
    // fallback colors if CSS variables are not defined (matches the nav-o0 base below)
    GodstransportFallback: "#507889",
    PersontransportFallback: "#727800",
    ProsessindustriFallback: "#b35013",
    PetroleumFallback: "#5f7775",
    ØvrigFallback: "#068285",

    // Level 1 sectors. Some fills carry the white nav-card heading/paragraph text, which needs
    // 4.5:1 (WCAG 1.4.3). The Balder visualisation ramp is mirrored between themes (level N in
    // light mode ~= level 20-N in dark mode, see colors.css), so a plain "base + offset" level
    // gets darker in light mode but LIGHTER in dark mode as offset grows - no single text color
    // stays readable against that. These "-nav-oN" tokens (defined in colors.css) route each
    // offset to a level that stays equally dark in both themes, anchored at o0 = visualisation-10,
    // the lightest level where both white text and the fill-vs-page contrast hold everywhere.
    Godstransport: "--ds-color-sjobla-nav-o0",
    Persontransport: "--ds-color-solgul-nav-o0",
    Prosessindustri: "--ds-color-kraftoransje-nav-o0",
    Petroleum: "--ds-color-fjellgronn-nav-o0",
    Øvrig: "--ds-color-himmelbla-nav-o0",
};

export const strokeColors: { [name: string]: string } = {
    // Level 1 sectors. Strokes start 4 levels darker than fills so they stay visibly
    // distinct while both sides of the fill/stroke pair clear the 3:1 contrast minimum.
    // Routed through "-stroke-oN" (colors.css) for the same reason fills use "-nav-oN":
    // without it, strokes would get LIGHTER than their fill as the offset grows in dark mode.
    Godstransport: "--ds-color-sjobla-stroke-o0",
    Persontransport: "--ds-color-solgul-stroke-o0",
    Prosessindustri: "--ds-color-kraftoransje-stroke-o0",
    Petroleum: "--ds-color-fjellgronn-stroke-o0",
    Øvrig: "--ds-color-himmelbla-stroke-o0",
};

export const OFFSET_BAR_COLOR_ENOVA_YEARLY_CHART = 0;
export const OFFSET_LINE_COLOR_ENOVA_YEARLY_CHART = 4;
export const OFFSET_PREVIOUS_COLOR_ENOVA_ACCUMULATED_CHART = 4;
export const OFFSET_CURRENT_COLOR_ENOVA_ACCUMULATED_CHART = 0;

const visualizationTokenRegex = /^(--ds-color-[a-zA-Z0-9-]+-visualisation-)(\d+)$/;

const getVisualizationToken = (baseToken: string, offset: number, maxLevel?: number) => {
    const match = baseToken.match(visualizationTokenRegex);
    if (!match) return baseToken;
    const baseLevel = Number.parseInt(match[2], 10);
    const unclamped = baseLevel + offset;
    const upperBound = maxLevel ?? unclamped;
    const targetLevel = Math.max(1, Math.min(unclamped, upperBound));
    return `${match[1]}${targetLevel}`;
};

export const getVisualizationColor = (baseToken: string, fallback: string, offset = 0, maxLevel?: number) => {
    const token = getVisualizationToken(baseToken, offset, maxLevel);
    return `var(${token}, ${fallback})`;
};

// Fills and strokes route through the "-nav-oN" / "-stroke-oN" tokens in colors.css (see
// fillColors/strokeColors comments above) so the same offset stays equally dark in both
// themes. Only o0-MAX are defined per family; beyond that the CSS var doesn't exist and
// resolves to the fallback hex, so offsets are clamped to that range.
const mirroredOffsetTokenRegex = /^(--ds-color-[a-zA-Z0-9-]+-(?:nav|stroke)-o)(\d+)$/;
const MAX_NAV_FILL_OFFSET = 6;
const MAX_STROKE_OFFSET = 5;

const getMirroredOffsetToken = (baseToken: string, offset: number, maxOffset: number) => {
    const match = baseToken.match(mirroredOffsetTokenRegex);
    if (!match) return baseToken;
    const targetOffset = Math.max(0, Math.min(offset, maxOffset));
    return `${match[1]}${targetOffset}`;
};

export const getSystemFillColor = (system: string, offset = 0) => {
    const baseToken = fillColors[system];
    const fallback = fillColors[`${system}Fallback`] || "#cccccc";
    if (!baseToken) return fallback;
    const token = getMirroredOffsetToken(baseToken, offset, MAX_NAV_FILL_OFFSET);
    return `var(${token}, ${fallback})`;
};

export const getSystemStrokeColor = (system: string, offset = 0) => {
    const baseToken = strokeColors[system];
    const fallback = fillColors[`${system}Fallback`] || "#cccccc";
    if (!baseToken) return fallback;
    const token = getMirroredOffsetToken(baseToken, offset, MAX_STROKE_OFFSET);
    return `var(${token}, ${fallback})`;
};

// Ranking year used when assigning rank-based colors for level 3 and matching level 4.
export const LEVEL3_COLOR_RANK_YEAR = 2024;

// Base color offset per top-level system for level 1 nav cards.
export const LEVEL1_COLOR_OFFSET_BY_SYSTEM: Record<string, number> = {
    Godstransport: 0,
    Persontransport: 0,
    Prosessindustri: 0,
    Petroleum: 0,
    Øvrig: 0,
};

// Hover color offset difference applied to all levels when hovering over nav items (can be 0 for no change, or positive/negative for stronger/weaker hover effect).
// Note that level 3 and 4 have max level constraints based on the ranking year, so be mindful when adjusting this.
export const NAV_HOVER_LEVEL_DIFF = 0;

// Shared transition for nav card background state changes.
export const NAV_CARD_BACKGROUND_TRANSITION = "background 150ms ease, opacity 150ms ease";

export type NavColors = {
    fill: string;
    hoverFill: string;
    stroke: string;
    hoverStroke: string;
};

export type EmissionsLineThemeColors = {
    withoutEnova: string;
    klimaMal: string;
};

// Resolves theme-aware line colors for the emissions chart (light/dark compatible).
export const getEmissionsLineThemeColors = (): EmissionsLineThemeColors => ({
    withoutEnova: getThemeAwareCssValue("--ds-color-fjellgronn-visualisation-16", "--ds-color-fjellgronn-text-default"),
    klimaMal: getThemeAwareCssValue("--ds-color-fjellgronn-visualisation-16", "--ds-color-fjellgronn-text-default"),
});

// Shared tooltip cursor style with stronger contrast in light mode.
export const getTooltipCursorStyle = () => ({
    fill: getThemeAwareCssValue("rgba(16, 38, 37, 0.14)", "rgba(255, 255, 255, 0.12)"),
    stroke: getThemeAwareCssValue("rgba(16, 38, 37, 0.32)", "rgba(255, 255, 255, 0.28)"),
    strokeWidth: 1,
});

// Geometry for the dotted effect pattern reused by card/chart visuals.
export const EFFECT_PATTERN = {
    dotRadiusPx: 1.25,
    tileSizePx: 8,
    firstDotCenterPx: 2,
    secondDotCenterPx: 6,
};

export const EFFECT_PATTERN_DOT_COLOR_DEFAULT = "var(--effect-card-pattern-dot, rgba(230, 233, 233, 0.65))";
export const EFFECT_PATTERN_DOT_COLOR_HOVER = "var(--effect-card-pattern-dot-hover, rgba(211, 211, 211, 0.6))";
export const EFFECT_PATTERN_BACKGROUND_COLOR = "var(--effect-card-background, var(--ds-color-surface-default, #fff))";

// Switches pattern dot color based on hover state.
export const getEffectPatternDotColor = (isHovered: boolean) =>
    isHovered ? EFFECT_PATTERN_DOT_COLOR_HOVER : EFFECT_PATTERN_DOT_COLOR_DEFAULT;

// Builds a reusable background style object for the dotted effect pattern.
export const getEffectPatternBackgroundStyle = (dotColor: string) => {
    const { firstDotCenterPx, secondDotCenterPx, dotRadiusPx, tileSizePx } = EFFECT_PATTERN;
    return {
        backgroundImage: `radial-gradient(circle at ${firstDotCenterPx}px ${firstDotCenterPx}px, ${dotColor} ${dotRadiusPx}px, transparent ${dotRadiusPx}px), radial-gradient(circle at ${secondDotCenterPx}px ${secondDotCenterPx}px, ${dotColor} ${dotRadiusPx}px, transparent ${dotRadiusPx}px)`,
        backgroundSize: `${tileSizePx}px ${tileSizePx}px`,
        backgroundColor: EFFECT_PATTERN_BACKGROUND_COLOR,
    } as const;
};

// Centralized nav color builder so all levels use the same fill/stroke rules.
export const createNavColors = (system: string, offset: number): NavColors => ({
    fill: getSystemFillColor(system, offset),
    hoverFill: getSystemFillColor(system, offset - NAV_HOVER_LEVEL_DIFF),
    stroke: getSystemStrokeColor(system, offset),
    hoverStroke: getSystemStrokeColor(system, offset - NAV_HOVER_LEVEL_DIFF),
});
